import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../api/admin';

export default function AdminPage() {
  const { user, accessToken, loading } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats]               = useState(null);
  const [users, setUsers]               = useState(null);
  const [loadingData, setLoadingData]   = useState(true);
  const [error, setError]               = useState('');
  const [backupBusy, setBackupBusy]     = useState(false);
  const [backupMsg, setBackupMsg]       = useState('');

  useEffect(() => {
    if (!accessToken || !user || user.role !== 'Admin') return;
    (async () => {
      try {
        const [s, u] = await Promise.all([
          adminApi.stats(accessToken),
          adminApi.users(accessToken),
        ]);
        setStats(s);
        setUsers(u);
      } catch (err) {
        setError(err.message || 'Failed to load admin data.');
      } finally {
        setLoadingData(false);
      }
    })();
  }, [accessToken, user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'Admin') return <Navigate to="/" replace />;

  async function handleBackup() {
    setBackupBusy(true);
    setBackupMsg('');
    try {
      const filename = await adminApi.downloadBackup(accessToken);
      setBackupMsg(`Backup downloaded: ${filename}`);
    } catch (err) {
      setBackupMsg(`Failed: ${err.message}`);
    } finally {
      setBackupBusy(false);
    }
  }

  async function handleDeleteUser(u, e) {
    e.stopPropagation();
    const confirmText =
      `Permanently delete user "${u.email}"?\n\n` +
      `This will also delete:\n` +
      `  • all of their portfolios\n` +
      `  • all of their transactions\n` +
      `  • all of their auth tokens (login/reset/invite)\n\n` +
      `This action CANNOT be undone. Type DELETE to confirm.`;
    const answer = window.prompt(confirmText);
    if (answer !== 'DELETE') return;
    try {
      const result = await adminApi.deleteUser(accessToken, u.id);
      setUsers(prev => prev.filter(x => x.id !== u.id));
      window.alert(
        `Deleted ${result.email}. ` +
        `Cascaded: ${result.cascadedPortfolios} portfolio(s), ${result.cascadedTransactions} transaction(s).`
      );
      // Refresh stats so counters update
      try {
        const s = await adminApi.stats(accessToken);
        setStats(s);
      } catch { /* non-critical */ }
    } catch (err) {
      window.alert(`Delete failed: ${err.message}`);
    }
  }

  return (
    <div style={s.wrap}>
      <h1 style={s.h1}>Admin Tools</h1>
      <p style={s.sub}>Restricted area · signed in as {user.email}</p>

      {error && <div style={s.error}>{error}</div>}

      {/* Backup */}
      <section style={s.card}>
        <div style={s.cardHead}>
          <h2 style={s.h2}>Database backup</h2>
          <button
            onClick={handleBackup}
            disabled={backupBusy}
            style={{ ...s.btn, opacity: backupBusy ? 0.6 : 1 }}
          >
            {backupBusy ? 'Preparing…' : 'Download backup (.zip)'}
          </button>
        </div>
        <p style={s.muted}>
          Exports every table as JSON inside a ZIP archive. Store it somewhere safe — this is your full off-Railway copy.
        </p>
        {backupMsg && <div style={s.note}>{backupMsg}</div>}
      </section>

      {/* Stats */}
      <section style={s.card}>
        <h2 style={s.h2}>Stats</h2>
        {loadingData ? (
          <div style={s.muted}>Loading…</div>
        ) : stats ? (
          <div style={s.statsGrid}>
            <Stat label="Users (total)"   value={stats.users} />
            <Stat label="Active users"    value={stats.activeUsers} />
            <Stat label="Pending invites" value={stats.pendingUsers} />
            <Stat label="Portfolios"      value={stats.portfolios} />
            <Stat label="Transactions"    value={stats.transactions} />
            <Stat label="Instruments"     value={stats.instruments} />
            <Stat label="Price rows"      value={stats.priceRows} />
            <Stat label="FX rows"         value={stats.fxRows} />
            <Stat label="Fundamentals"    value={stats.fundamentalsRows} />
          </div>
        ) : null}
      </section>

      {/* Users */}
      <section style={s.card}>
        <h2 style={s.h2}>Users</h2>
        {loadingData ? (
          <div style={s.muted}>Loading…</div>
        ) : users && users.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>ID</th>
                  <th style={s.th}>Email</th>
                  <th style={s.th}>Role</th>
                  <th style={s.th}>Active</th>
                  <th style={s.th}>Tier</th>
                  <th style={s.th}>Max P.</th>
                  <th style={s.th}># P.</th>
                  <th style={s.th}>Created</th>
                  <th style={{ ...s.th, textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr
                    key={u.id}
                    onClick={() => navigate(`/admin/users/${u.id}`)}
                    style={s.trClickable}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-strong)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={s.td}>{u.id}</td>
                    <td style={s.td}>{u.email}</td>
                    <td style={s.td}>{u.role}</td>
                    <td style={s.td}>{u.isActive ? '✓' : '—'}</td>
                    <td style={s.td}>{u.tier || 'Free'}</td>
                    <td style={s.td}>{u.maxPortfolios}</td>
                    <td style={s.td}>{u.portfolioCount}</td>
                    <td style={s.td}>{new Date(u.createdAt).toLocaleString()}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '6px', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); navigate(`/admin/users/${u.id}`); }}
                          style={s.iconBtn}
                          title="Manage user"
                          aria-label="Manage user"
                        >
                          <GearIcon />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleDeleteUser(u, e)}
                          style={s.dangerIconBtn}
                          title="Delete user"
                          aria-label="Delete user"
                          disabled={u.id === user.id}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={s.muted}>No users.</div>
        )}
      </section>
    </div>
  );
}

function GearIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
      <line x1="10" y1="11" x2="10" y2="17"/>
      <line x1="14" y1="11" x2="14" y2="17"/>
    </svg>
  );
}

function Stat({ label, value }) {
  return (
    <div style={s.statBox}>
      <div style={s.statLabel}>{label}</div>
      <div style={s.statValue}>{value ?? '—'}</div>
    </div>
  );
}

const s = {
  wrap:        { maxWidth: '1100px', margin: '0 auto', padding: '32px 24px', color: 'var(--text-primary)' },
  h1:          { fontSize: '28px', fontWeight: 700, margin: '0 0 4px' },
  sub:         { color: 'var(--text-secondary)', fontSize: '14px', margin: '0 0 28px' },
  card:        { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px 22px', marginBottom: '20px', boxShadow: 'var(--shadow)' },
  cardHead:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' },
  h2:          { fontSize: '18px', fontWeight: 700, margin: '0 0 8px' },
  muted:       { color: 'var(--text-secondary)', fontSize: '14px' },
  note:        { marginTop: '12px', padding: '10px 12px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '8px', fontSize: '13px' },
  error:       { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', padding: '12px 14px', color: '#ef4444', fontSize: '14px', marginBottom: '20px' },
  btn:         { padding: '10px 16px', borderRadius: '10px', border: 'none', background: '#6366f1', color: '#fff', fontSize: '14px', fontWeight: 700, cursor: 'pointer' },
  statsGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', marginTop: '8px' },
  statBox:     { background: 'var(--surface-strong)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 14px' },
  statLabel:   { fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' },
  statValue:   { fontSize: '22px', fontWeight: 700 },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th:          { textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' },
  td:          { padding: '10px 8px', borderBottom: '1px solid var(--border)' },
  trClickable: { cursor: 'pointer', transition: 'background 0.15s' },
  iconBtn:     { background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', width: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-secondary)' },
  dangerIconBtn: { background: 'transparent', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '8px', width: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444' },
};
